import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import type { EnrollmentTrendPoint } from '../../lib/api';
import { TenantWideBadge } from './tenant-wide-badge';

/**
 * Daily new-slot count over a 90-day window. Light line, axis tickers small
 * to match the muted-foreground tone. Sparse data is OK — the line just
 * stays near zero until activations show up.
 *
 * Always tenant-wide: `slots` carries no branch column — genealogy is
 * deliberately branch-free in this schema.
 */
export function EnrollmentTrendChart({
  series,
  caption = 'New members / day, last 90d',
  filtered = false,
}: {
  series: EnrollmentTrendPoint[];
  caption?: string;
  filtered?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-h3">
          <span>
            Enrollment trend{' '}
            <span className="font-normal text-caption text-muted-foreground">{caption}</span>
          </span>
          <TenantWideBadge show={filtered} />
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 py-2">
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => v.slice(8, 10)}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickMargin={6}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                allowDecimals={false}
                width={36}
              />
              <Tooltip
                cursor={{ stroke: 'hsl(var(--border))' }}
                contentStyle={{
                  background: 'hsl(var(--popover))',
                  color: 'hsl(var(--popover-foreground))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  boxShadow: 'var(--shadow-elevated)',
                  fontSize: 12,
                }}
                labelStyle={{ color: 'hsl(var(--muted-foreground))' }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.75}
                dot={false}
                activeDot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
